import React, { useState } from 'react'
import { Box, Typography, styled } from '@mui/material'
import { Button, Input, Modal } from '@/design-system'
import { fieldInputSx } from '../SettingsField'

const Copy = styled(Typography)({
  fontSize: 13.5,
  color: 'var(--ds-ink-muted)',
  lineHeight: 1.6,
  marginBottom: 16,
})

const Prompt = styled('div')({
  fontSize: 12,
  color: 'var(--ds-ink-subtle)',
  marginBottom: 8,
  '& b': { color: 'var(--ds-ink)', fontFamily: 'var(--ds-font-mono)' },
})

export interface ConfirmDangerModalProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description: string
  /** The exact phrase the user must type to enable the confirm action */
  confirmPhrase: string
  actionLabel: string
  loading?: boolean
  /** `amber` = reversible (deactivate), `coral` = permanent (delete) — matches `.btn-amber` / `.btn-danger` in the mock */
  tone?: 'amber' | 'coral'
}

const TONE_SX = {
  amber: {
    boxShadow: 'inset 0 0 0 1px rgba(255,207,125,0.7)',
    color: '#92600a',
    backgroundColor: '#fff',
    '&:hover': { boxShadow: 'inset 0 0 0 1px #92600a' },
    '&.Mui-disabled': { boxShadow: 'inset 0 0 0 1px rgba(255,207,125,0.7)', color: '#92600a', backgroundColor: '#fff' },
  },
  coral: {
    backgroundColor: 'var(--ds-accent-deep-a)',
    color: '#fff',
    '&:hover': { backgroundColor: 'var(--ds-accent-deep-a)', opacity: 0.88 },
    '&.Mui-disabled': { backgroundColor: 'var(--ds-accent-deep-a)', color: '#fff' },
  },
}

/** Type-to-confirm modal shared by Deactivate/Delete — same safety gate the legacy modals used. */
export function ConfirmDangerModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmPhrase,
  actionLabel,
  loading,
  tone = 'coral',
}: ConfirmDangerModalProps) {
  const [value, setValue] = useState('')

  const handleClose = () => {
    setValue('')
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose} title={title}>
      <Copy>{description}</Copy>
      <Prompt>
        Type <b>{confirmPhrase}</b> to confirm
      </Prompt>
      <Input
        variant='filled'
        fullWidth
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={confirmPhrase}
        autoFocus
        sx={{ ...fieldInputSx, marginBottom: 2 }}
      />
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button variant='ghost' size='sm' onClick={handleClose}>
          Cancel
        </Button>
        <Button
          size='sm'
          disabled={value !== confirmPhrase}
          loading={loading}
          onClick={onConfirm}
          sx={TONE_SX[tone]}
        >
          {actionLabel}
        </Button>
      </Box>
    </Modal>
  )
}
